import {AfterContentInit, Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {BusyIndicatorService} from '../../../../../components/busy-indicator/busy-indicator.service';
import {BusinessDataService} from '../../../../../data-repositories/business-data.service';
import {LanguageService} from '../../../../../data-repositories/language.service';
import {AgreementDocument} from '../../../../../models/agreement-document';
import {GeneralAgreement} from '../../../../../models/general-agreement';
import {User} from '../../../../../models/user';
import {AuthService} from '../../../../../repositories/auth.service';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {StagesNavigationService} from '../../../stages-navigation/stages-navigation.service';
import {MarginTypeService} from '../../../../../services/margin-type.service';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-general-agreement',
  templateUrl: './general-agreement.component.html',
  styleUrls: ['./general-agreement.component.scss']
})
export class GeneralAgreementComponent implements OnInit, OnDestroy, AfterContentInit {
  public formsModels: Array<GeneralAgreement> = new Array<GeneralAgreement>();
  public generalAgreements: Array<GeneralAgreement> = new Array<GeneralAgreement>();
  public generalDisclousres: Array<GeneralAgreement> = new Array<GeneralAgreement>();
  public isAgree = null;
  public isAdditionalGeneralAgreements = null;
  public margin = true;
  public userModel: User = new User();
  public isOpenedDisclosures = false;
  public isOpenedAgreements = false;

  public agreementFrom: FormGroup;

  public submitted: boolean;
  public agreementsInfoIsOpen: boolean;
  public agreementsInfo: string;
  agreements: AgreementDocument[];
  token: string;
  public descriptionStates = new Array<boolean>();
  private subscription: Subscription;
  private formSub = new Subscription();

  constructor(
    private service: BusinessDataService,
    private formBuilder: FormBuilder,
    private validationService: ValidationInfoService,
    public langService: LanguageService,
    public navigationService: StagesNavigationService,
    private busyService: BusyIndicatorService,
    private marginTypeService: MarginTypeService,
    authenticationService: AuthService) {

    this.token = authenticationService.accessToken;

    this.isAgree = false;
    this.isAdditionalGeneralAgreements = false;

    this.formSub.add(this.langService.getInfoFieldSubject().subscribe(ifm => {
      this.agreementsInfo = ifm.find(field => field.key === 'General Agreements and Disclosures').info;
    }));

    this.formSub.add(service.GetUser().subscribe(s => {
      this.userModel = s.model;

      if (this.isAgree == null) {
        this.isAgree = this.userModel.generalAgreements && this.userModel.generalAgreements.every(x => x.isAgree);
      }
    }));

    this.formSub.add(this.langService.getAgreements().subscribe(a => {
      this.agreements = a;
    }));

    this.subscription = this.validationService.onUserUpdate.subscribe(() => {
      this.submitted = true;
      this.markAllFieldsAsTouched();
      this.validationService.validate(this.agreementFrom.valid, 'GeneralAgreementComponent');
    });
  }

  get isMobile(): boolean {
    return window.innerWidth < 990;
  }

  ngAfterContentInit(): void {
    this.busyService.beginProcess();
    this.service.updateAgreements(x => {

      this.formsModels = x;

      this.generalAgreements = this.formsModels.filter(frm => !frm.formDoc.isDisclosure);
      this.generalDisclousres = this.formsModels.filter(frm => frm.formDoc.isDisclosure);
      this.busyService.endProcess();
    });
  }

  acceptGeneralAgreements() {
    this.userModel.generalAgreements = this.formsModels;
    this.updateUser();
  }

  acceptAdditionalGeneralAgreements() {
    this.userModel.isAdditionalGeneralAgreements = this.isAdditionalGeneralAgreements;
    this.updateUser(() => {
      this.userModel.isAdditionalGeneralAgreements = false;
    });
  }

  toggleDisclosure() {
    this.isOpenedDisclosures = !this.isOpenedDisclosures;
  }

  toggleAgreements() {
    this.isOpenedAgreements = !this.isOpenedAgreements;
  }

  downloadForm(formNo: string, fromName: string) {
    this.service.downloadForm(formNo, fromName);
  }

  ngOnInit() {
    this.createForm();
    this.patchFormFromModel();
    this.formSub.add(this.agreementFrom.valueChanges.subscribe(() => this.syncModelFromForm()));
  }

  public isRequiredField(fieldName: string): boolean {
    return this.agreementFrom.controls[fieldName].invalid
      && ((this.agreementFrom.controls[fieldName].dirty || this.agreementFrom.controls[fieldName].touched) || this.submitted)
      && this.agreementFrom.controls[fieldName].errors.required;
  }

  public downloadDisclosure() {
    this.service.downloadDisclosures();
  }

  public downloadAgreemetns() {
    this.service.downloadAgreements();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.formSub.unsubscribe();
  }

  public updateUser(callback: () => void = () => {
  }): void {
    this.service.updateUserWithoutValidation(this.userModel, callback);
  }

  public switchDescriptionState(event: Event, key: string) {
    event.stopPropagation();

    if (this.descriptionStates[key] === undefined) {
      this.descriptionStates[key] = true;
      return;
    }

    this.descriptionStates[key] = !this.descriptionStates[key];
  }

  getFormDoc(formNo: string) {
    return this.agreements.find(x => x.formNo == formNo);
  }

  getMarginType(checkTrades: boolean = true) {
    return this.marginTypeService.getMarginType(this.userModel, checkTrades, false);
  }

  private createForm() {
    this.agreementFrom = this.formBuilder.group({
      isAgree: new FormControl(false, Validators.requiredTrue),
      isAdditionalAgree: new FormControl(false, Validators.requiredTrue),
      syep: new FormControl(true),
      margin: new FormControl(true, validateOptionalRequiredTrue(() => this.getMarginType() === 'Margin'))
    });
  }

  private patchFormFromModel(): void {
    if (!this.userModel || !this.agreementFrom) {
      return;
    }
    this.agreementFrom.patchValue({
      margin: this.margin ?? null,
      syep: this.userModel.stockYieldProgram ?? null,
      isAgree: this.isAgree ?? null,
      isAdditionalAgree: this.isAdditionalGeneralAgreements ?? null
    }, {emitEvent: false});
    formLog.state('GeneralAgreement', this.userModel, this.agreementFrom);
  }

  private syncModelFromForm(): void {
    if (!this.userModel) {
      return;
    }
    const value = this.agreementFrom.getRawValue();
    this.margin = value.margin;
    this.userModel.stockYieldProgram = value.syep;
    this.isAgree = value.isAgree;
    this.isAdditionalGeneralAgreements = value.isAdditionalAgree;
    formLog.state('GeneralAgreement', this.userModel, this.agreementFrom);
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.agreementFrom.controls).forEach(key => {
      this.agreementFrom.controls[key].markAsTouched();
      this.agreementFrom.controls[key].updateValueAndValidity();
    });
  }

  private getAge(dateOfBirth: Date): number {
    const today = new Date();
    const a = (today.getFullYear() * 100 + today.getMonth()) * 100 + today.getDate();
    const b = (dateOfBirth.getFullYear() * 100 + dateOfBirth.getMonth()) * 100 + dateOfBirth.getDate();
    return (a - b) / 10000;
  }
}

export function validateOptionalRequiredTrue(isRequired: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return isRequired() ? Validators.requiredTrue(control) : null;
  };
}
